import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';

// @ts-ignore: This package isn't typescript-friendly
import { saveAs } from 'filesaver.js-npm';
import ExcelJS from 'exceljs';
import moment from 'moment';

import { handleServerError } from 'globals/utils/handleServerError';
import { MimeTypes } from 'core/enums';
import {
  LearnerMatchingMetricsService,
  LearnerMatchingMetricsQuery,
  LearnerMatchingMetricsResponse,
} from 'services/LearnerMatchingMetricsService';
import { AppState } from 'store';
import { popToast } from 'store/toast/actions';
import { errorToastOptions, infoToastOptions, successToastOptions } from 'store/toast/constants';
import {
  DOWNLOAD_LEARNER_MATCHING_METRICS,
  DOWNLOAD_LEARNER_MATCHING_METRICS_FAILURE,
  DOWNLOAD_LEARNER_MATCHING_METRICS_SUCCESS,
  LearnerMatchingMetricsActionTypes,
} from './types';

const HEADER_FONT: Partial<ExcelJS.Font> = { bold: true, size: 11, color: { theme: 1 }, name: 'Calibri', family: 2 };
const DATA_FONT: Partial<ExcelJS.Font> = { size: 11, color: { theme: 1 }, name: 'Calibri', family: 2 };
const THIN_BORDER: Partial<ExcelJS.Border> = { style: 'thin' };
const HEADER_BORDER: Partial<ExcelJS.Borders> = {
  left: THIN_BORDER,
  right: THIN_BORDER,
  top: THIN_BORDER,
  bottom: THIN_BORDER,
};

function applyHeaderCell(
  cell: ExcelJS.Cell,
  value: string,
  alignment: Partial<ExcelJS.Alignment> = { horizontal: 'center' },
) {
  cell.value = value;
  cell.font = HEADER_FONT;
  cell.border = HEADER_BORDER;
  cell.alignment = alignment;
}

async function buildExcelReport(data: LearnerMatchingMetricsResponse[], startDate: Date, endDate: Date): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Report');

  // Column widths
  ws.getColumn(1).width = 52.89;
  ws.getColumn(2).width = 15.78;
  ws.getColumn(3).width = 14.89;
  ws.getColumn(4).width = 11;
  ws.getColumn(5).width = 11.78;
  ws.getColumn(6).width = 11;
  ws.getColumn(7).width = 13;

  // Row 2: Reporting Dates
  const startFormatted = moment(startDate).format('MM/DD/YYYY');
  const endFormatted = moment(endDate).format('MM/DD/YYYY');
  ws.getRow(2).getCell(1).value = {
    richText: [
      { font: { ...DATA_FONT, bold: true }, text: 'Reporting Dates:' },
      { font: DATA_FONT, text: ` ${startFormatted} thru ${endFormatted}` },
    ],
  };

  // Row 5: Group headers (MOC / CME) — merged across sub-columns
  ws.mergeCells('A5:A8');
  applyHeaderCell(ws.getCell('A5'), 'Accredited Provider', { horizontal: 'center', vertical: 'middle' });

  ws.mergeCells('B5:D5');
  applyHeaderCell(ws.getCell('B5'), 'MOC');

  ws.mergeCells('E5:G5');
  applyHeaderCell(ws.getCell('E5'), 'CME');

  // Rows 6-8: Sub-column headers (merged vertically)
  const subColAlignment: Partial<ExcelJS.Alignment> = { horizontal: 'center', vertical: 'middle', wrapText: true };

  ws.mergeCells('B6:B8');
  applyHeaderCell(ws.getCell('B6'), '# of Learner Submissions Matched', subColAlignment);

  ws.mergeCells('C6:C8');
  applyHeaderCell(ws.getCell('C6'), '# of Learner Submissions Not Matched', subColAlignment);

  ws.mergeCells('D6:D8');
  applyHeaderCell(ws.getCell('D6'), '% of Learner Not Matched', subColAlignment);

  ws.mergeCells('E6:E8');
  applyHeaderCell(ws.getCell('E6'), '# of Learner Submissions Matched', subColAlignment);

  ws.mergeCells('F6:F8');
  applyHeaderCell(ws.getCell('F6'), '# of Learner Submissions Not Matched', subColAlignment);

  ws.mergeCells('G6:G8');
  applyHeaderCell(ws.getCell('G6'), '% of Learner Submissions Not Matched', subColAlignment);

  // Data rows start at row 9
  const centerAlignment: Partial<ExcelJS.Alignment> = { horizontal: 'center' };
  data.forEach((row, i) => {
    const rowNum = 9 + i;
    ws.getRow(rowNum).getCell(1).value = row.providerName;

    const mocNotMatched = row.totalSubmissionCountMOC - row.totalMOCMatchedCount;
    const mocPctNotMatched = row.totalSubmissionCountMOC > 0 ? mocNotMatched / row.totalSubmissionCountMOC : 0;

    const mocMatchedCell = ws.getRow(rowNum).getCell(2);
    mocMatchedCell.value = row.totalMOCMatchedCount;
    mocMatchedCell.font = DATA_FONT;
    mocMatchedCell.alignment = centerAlignment;

    const mocNotMatchedCell = ws.getRow(rowNum).getCell(3);
    mocNotMatchedCell.value = mocNotMatched;
    mocNotMatchedCell.font = DATA_FONT;
    mocNotMatchedCell.alignment = centerAlignment;

    const mocPctCell = ws.getRow(rowNum).getCell(4);
    mocPctCell.value = mocPctNotMatched;
    mocPctCell.numFmt = '0.00%';
    mocPctCell.font = DATA_FONT;
    mocPctCell.alignment = centerAlignment;

    const cmeNotMatched = row.totalSubmissionCount - row.totalCMEMatchedCount;
    const cmePctNotMatched = row.totalSubmissionCount > 0 ? cmeNotMatched / row.totalSubmissionCount : 0;

    const cmeMatchedCell = ws.getRow(rowNum).getCell(5);
    cmeMatchedCell.value = row.totalCMEMatchedCount;
    cmeMatchedCell.font = DATA_FONT;
    cmeMatchedCell.alignment = centerAlignment;

    const cmeNotMatchedCell = ws.getRow(rowNum).getCell(6);
    cmeNotMatchedCell.value = cmeNotMatched;
    cmeNotMatchedCell.font = DATA_FONT;
    cmeNotMatchedCell.alignment = centerAlignment;

    const cmePctCell = ws.getRow(rowNum).getCell(7);
    cmePctCell.value = cmePctNotMatched;
    cmePctCell.numFmt = '0.00%';
    cmePctCell.font = DATA_FONT;
    cmePctCell.alignment = centerAlignment;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: MimeTypes.XLSX });
}

// Action creators
const downloadLearnerMatchingMetricsAction = (): LearnerMatchingMetricsActionTypes => ({
  type: DOWNLOAD_LEARNER_MATCHING_METRICS,
});

const downloadLearnerMatchingMetricsSuccessAction = (): LearnerMatchingMetricsActionTypes => ({
  type: DOWNLOAD_LEARNER_MATCHING_METRICS_SUCCESS,
});

const downloadLearnerMatchingMetricsFailureAction = (payload: string): LearnerMatchingMetricsActionTypes => ({
  type: DOWNLOAD_LEARNER_MATCHING_METRICS_FAILURE,
  payload,
});

// Thunk action: fetches data then generates and downloads the Excel report
export const downloadLearnerMatchingMetrics =
  (params: LearnerMatchingMetricsQuery): ThunkAction<Promise<void>, AppState, null, AnyAction> =>
  async (dispatch): Promise<void> => {
    dispatch(downloadLearnerMatchingMetricsAction());
    dispatch(popToast({ ...infoToastOptions, message: <>Downloading...</> }));
    try {
      const data = await LearnerMatchingMetricsService.getAggregated(params);
      if (!data || data.length === 0) {
        dispatch(popToast({ ...errorToastOptions, message: <>There is no data in the Date Range selected</> }));
        dispatch(downloadLearnerMatchingMetricsSuccessAction());
        return;
      }
      const blob = await buildExcelReport(data, params.startDate, params.endDate);
      const startLabel = moment(params.startDate).format('YYYY-MM-DD');
      const endLabel = moment(params.endDate).format('YYYY-MM-DD');
      saveAs(blob, `LearnerMatchingMetrics_${startLabel}_to_${endLabel}.xlsx`);
      dispatch(downloadLearnerMatchingMetricsSuccessAction());
      dispatch(popToast({ ...successToastOptions, message: <>Downloaded successfully.</> }));
    } catch (error) {
      const { errorMessage } = handleServerError({ error, thunkName: 'downloadLearnerMatchingMetrics' });
      dispatch(downloadLearnerMatchingMetricsFailureAction(errorMessage));
      dispatch(
        popToast({
          ...errorToastOptions,
          message: <>{errorMessage || 'Error downloading Learner Matching Metrics.'}</>,
        }),
      );
    }
  };
